import React from 'react';
import { Initials } from '@fabric-ui/components';
import { DeliveryMethodOptions, FileFormat } from '@fabric-ui/components/Table';
import { flex } from '@fabric-ui/theme';
import { Box, SxProps } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useAvContext } from '../../context/AvContextProvider';
import { UserPermissionType } from '../CustomDashboards/types';
import { getDestinationDisplayName } from '../Outegrations/EditOutegration/config/config';
import { DeliveryConfig } from './types';

export const FieldsToFilter = ['finding_count', 'active_finding_count', 'asset_count', 'active_asset_count', 'alert_count'];

export const getNewDeliveryConfig = (deliveryConfig?: DeliveryConfig, defaultFormat = FileFormat.CSV_FORMAT) => {
  const deliveryConfigByType = exportDetails => ({
    [DeliveryMethodOptions.Email]: {
      integrationId: null,
      title: exportDetails.title,
      message: exportDetails.message,
      recipients: exportDetails.recipients,
    },
    [DeliveryMethodOptions.S3]: {
      integrationId: exportDetails.integrationId,
      path: exportDetails.path || '',
    },
    [DeliveryMethodOptions.GCP_STORAGE]: {
      integrationId: exportDetails.integrationId,
      path: exportDetails.path || '',
    },
    [DeliveryMethodOptions.SLACK]: {
      integrationId: exportDetails.integrationId,
      title: exportDetails.title,
      message: exportDetails.message,
      channelType: exportDetails.channelType,
      channel: exportDetails.channel,
    },
    [DeliveryMethodOptions.TEAMS]: {
      integrationId: exportDetails.integrationId,
      title: exportDetails.title,
      message: exportDetails.message,
      channelId: exportDetails.channelId,
    },
  });
  if (!deliveryConfig) {
    return {
      integrationId: null,
      type: DeliveryMethodOptions.Email,
      recipients: [],
      title: '',
      message: '',
      attachmentMetadata: {
        fileFormat: defaultFormat,
      },
    };
  }
  return {
    type: deliveryConfig.type,
    ...deliveryConfigByType(deliveryConfig)[deliveryConfig.type],
    attachmentMetadata: {
      fileFormat: deliveryConfig.attachmentMetadata?.fileFormat || defaultFormat,
    },
    compressFile: !!deliveryConfig.compressFile,
    compressType: deliveryConfig.compressType,
  };
};

export const emailIntegrationType = { id: null, name: '', integrationType: DeliveryMethodOptions.Email };

export const integrationsToOptions = ({ name, id, integrationType }) => ({
  title: `${getDestinationDisplayName(integrationType)}${name ? ` - ${name}` : ''}`,
  value: id,
});

export const isAllowedEditPermissionOnRow = row => row.userPermissionType === UserPermissionType.editor;
export const isSomeRowViewerOnly = rows =>
  rows.filter(v => v).length && rows.some(({ userPermissionType }) => userPermissionType === UserPermissionType.viewer);

export const findIntegration = (availableIntegrationOptions, exportDetails) =>
  availableIntegrationOptions.find(({ id }) => id === exportDetails.deliveryConfig?.integrationId);

export const INACTIVE_INTEGRATION_FOR_SCHEDULED_REPORT = 'The schedule export integration destination is not active';

export const convertValueToURI = value => encodeURIComponent(JSON.stringify(value));
export const convertURIToValue = uri => JSON.parse(decodeURIComponent(uri));

/**
 * Filters headCells to return only non-measurement fields (dimensions)
 * @param headCellsIds - Array of field IDs
 * @param measurementsAsObject - Object with measurement systemNames as keys
 * @returns Array of field IDs that are not measurements
 */
export const getHeadCellsWithoutMeasurements = (headCellsIds: string[], measurementsAsObject: Record<string, any>): string[] =>
  headCellsIds.filter(id => !measurementsAsObject[id]);

/**
 * Filters headCells to return only measurement fields (metrics)
 * @param headCellsIds - Array of field IDs
 * @param measurementsAsObject - Object with measurement systemNames as keys
 * @returns Array of field IDs that are measurements
 */
export const getHeadCellsMeasurements = (headCellsIds: string[], measurementsAsObject: Record<string, any>): string[] =>
  headCellsIds.filter(id => measurementsAsObject[id]);

const SET_ACTIVE = gql`
  mutation updateRuleSetActivationV3($ids: [String]!, $active: Boolean) {
    updateRuleSetActivationV3(ids: $ids, active: $active)
  }
`;

export const useToggleActivateRuleSetMutation = ({ refetch }: { refetch: () => void }) => {
  const { api } = useAvContext();
  const activateMutation = useMutation({
    mutationFn: (ids: string[]): Promise<any> => api(SET_ACTIVE, { options: { ids, active: true } }),
    onSuccess: refetch,
  });
  const deactivateMutation = useMutation({
    mutationFn: (ids: string[]): Promise<any> => api(SET_ACTIVE, { options: { ids, active: false } }),
    onSuccess: refetch,
  });

  return { activateMutation, deactivateMutation };
};

export const renderOption = ({
  title,
  iconFull = <Initials name={title} size="xSmall" />,
  sx = {},
}: {
  title: string;
  iconFull: React.JSX.Element;
  sx?: SxProps;
}) => (
  <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
    <Box sx={{ ...sx, borderRadius: '8px', ...flex.center }}>{iconFull}</Box>
    {title}
  </Box>
);
